// Tickets.jsx
import React, { useState, useEffect } from 'react';
import Sidebar from '../components/sidebar';
import Header from '../components/header';
import { apiCall } from '../utils/api';
import toast, { Toaster } from 'react-hot-toast';
import { FaSearch, FaUser, FaPhone, FaEnvelope, FaEye } from "react-icons/fa";

function Tickets() {
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [currentPage, setCurrentPage] = useState('dashboard');
    const [tickets, setTickets] = useState([]);
    const [filteredTickets, setFilteredTickets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showFilters, setShowFilters] = useState(true);

    // Modal states
    const [ticketModalOpen, setTicketModalOpen] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);

    // Pagination states
    const [pagination, setPagination] = useState({
        currentPage: 1,
        perPage: 10,
        totalRecords: 0,
        totalPages: 1
    });

    // Helper function to get date 7 days ago
    const getDate7DaysAgo = () => {
        const date = new Date();
        date.setDate(date.getDate() - 7);
        return date.toISOString().split('T')[0];
    };

    // Helper function to get current date
    const getCurrentDate = () => {
        return new Date().toISOString().split('T')[0];
    };

    // Filter states with default date range
    const [filters, setFilters] = useState({
        searchValue: '',
        status: '',
        queryType: '',
        dateFrom: getDate7DaysAgo(),
        dateTo: getCurrentDate()
    });

    // Query type options
    const queryOptions = [
        { id: '', name: 'All Queries' },
        { id: '1', name: 'Payment Issue' },
        { id: '2', name: 'Recharge Issue' },
        { id: '3', name: 'Bill Payment' },
        { id: '4', name: 'OTP Issue' },
        { id: '5', name: 'Delete Account' },
        { id: '6', name: 'Recover Account' },
        { id: '7', name: 'KYC Issue' },
        { id: '8', name: 'Chargeback' },
        { id: '9', name: 'Refund' }
    ];

    // Status options
    const statusOptions = [
        { id: '', name: 'All Status' },
        { id: '1', name: 'Pending' },
        { id: '2', name: 'Ongoing' },
        { id: '3', name: 'Resolved' },
        { id: '4', name: 'Rejected' },
        { id: '5', name: 'Refunded' },
        { id: '6', name: 'Chargeback User Own' },
        { id: '7', name: 'Chargeback User Loose' }
    ];

    // Fetch Tickets with pagination
    const fetchTickets = async (page = 1) => {
        setLoading(true);
        try {
            const params = {
                searchValue: filters.searchValue,
                status: filters.status,
                queryType: filters.queryType,
                dateFrom: filters.dateFrom,
                dateTo: filters.dateTo,
                page: page,
                limit: pagination.perPage
            };

            const result = await apiCall(
                "ticket",
                params,
                true
            );

            if (result.status === true) {
                setTickets(result.data || []);
                setFilteredTickets(result.data || []);
                if (result.pagination) {
                    setPagination(result.pagination);
                }
            } else {
                toast.error(result.message || "Failed to fetch tickets");
                setTickets([]);
                setFilteredTickets([]);
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
            setTickets([]);
            setFilteredTickets([]);
        } finally {
            setLoading(false);
        }
    };

    // Handle Ticket Click - View Ticket Details
    const handleTicketClick = (ticket) => {
        setSelectedTicket(ticket);
        setTicketModalOpen(true);
    };

    // Initial fetch
    useEffect(() => {
        fetchTickets(1);
    }, []);

    // Apply filters
    const applyFilters = () => {
        fetchTickets(1);
    };

    // Reset filters - resets to default date range
    const resetFilters = () => {
        setFilters({
            searchValue: '',
            status: '',
            queryType: '',
            dateFrom: getDate7DaysAgo(),
            dateTo: getCurrentDate()
        });
        fetchTickets(1);
    };

    // Handle page change
    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            fetchTickets(newPage);
        }
    };

    // Handle filter change
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // Get status color
    const getStatusColor = (status) => {
        const colors = {
            'Pending': 'bg-yellow-100 text-yellow-800',
            'Ongoing': 'bg-blue-100 text-blue-800',
            'Resolved': 'bg-green-100 text-green-800',
            'Rejected': 'bg-red-100 text-red-800',
            'Refunded': 'bg-purple-100 text-purple-800',
            'Chargeback User Own': 'bg-orange-100 text-orange-800',
            'Chargeback User Loose': 'bg-pink-100 text-pink-800'
        };
        return colors[status] || 'bg-gray-100 text-gray-800';
    };

    // Get query type color
    const getQueryColor = (query) => {
        const colors = {
            'Payment Issue': 'bg-red-100 text-red-800',
            'Recharge Issue': 'bg-blue-100 text-blue-800',
            'Bill Payment': 'bg-green-100 text-green-800',
            'OTP Issue': 'bg-purple-100 text-purple-800',
            'Delete Account': 'bg-gray-100 text-gray-800',
            'Recover Account': 'bg-indigo-100 text-indigo-800',
            'KYC Issue': 'bg-yellow-100 text-yellow-800',
            'Chargeback': 'bg-orange-100 text-orange-800',
            'Refund': 'bg-pink-100 text-pink-800'
        };
        return colors[query] || 'bg-gray-100 text-gray-800';
    };

    // Get action color
    const getActionColor = (action) => {
        const colors = {
            'Admin': 'bg-purple-100 text-purple-800',
            'Asst. Admin': 'bg-blue-100 text-blue-800',
            'Employee': 'bg-green-100 text-green-800',
            'Shareholder': 'bg-yellow-100 text-yellow-800',
            'Retailer': 'bg-indigo-100 text-indigo-800',
            'API': 'bg-red-100 text-red-800',
            'Agent': 'bg-orange-100 text-orange-800',
            'Callback API': 'bg-pink-100 text-pink-800',
            'Auto Sync': 'bg-gray-100 text-gray-800',
            'Status API': 'bg-cyan-100 text-cyan-800'
        };
        return colors[action] || 'bg-gray-100 text-gray-800';
    };

    return (
        <div className="bg-gray-50 font-sans antialiased min-h-screen">
            {/* Mobile Overlay */}
            {sidebarOpen && (
                <div
                    className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            <Sidebar
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                isOpen={sidebarOpen}
                setIsOpen={setSidebarOpen}
            />

            <div className="main-content lg:ml-[72px] transition-all duration-200">
                <Header
                    currentPage={currentPage}
                    sidebarOpen={sidebarOpen}
                    setSidebarOpen={setSidebarOpen}
                />

                <div className="p-4 sm:p-6 lg:p-8">
                    {/* Page Title */}
                    <div className="mb-6">
                        <h1 className="text-2xl font-bold text-gray-900">Tickets</h1>
                        <p className="text-gray-600 text-sm">View and manage all support tickets</p>
                    </div>

                    {/* Filter Section */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6 overflow-hidden">
                        <div className={`p-4 sm:p-6 ${showFilters ? 'block' : 'hidden lg:block'}`}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                                {/* Column 1: Search */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Search
                                    </label>
                                    <div className="relative">
                                        <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input
                                            type="text"
                                            value={filters.searchValue}
                                            onChange={(e) => handleFilterChange('searchValue', e.target.value)}
                                            placeholder="Search by description, user..."
                                            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                </div>

                                {/* Column 2: Date Range */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Date Range
                                    </label>
                                    <div className="flex gap-2">
                                        <input
                                            type="date"
                                            value={filters.dateFrom}
                                            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
                                            className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                        <input
                                            type="date"
                                            value={filters.dateTo}
                                            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
                                            className="w-1/2 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                        />
                                    </div>
                                </div>

                                {/* Column 3: Query Type */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Query Type
                                    </label>
                                    <select
                                        value={filters.queryType}
                                        onChange={(e) => handleFilterChange('queryType', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    >
                                        {queryOptions.map(option => (
                                            <option key={option.id} value={option.id}>
                                                {option.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Column 4: Status */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Status
                                    </label>
                                    <select
                                        value={filters.status}
                                        onChange={(e) => handleFilterChange('status', e.target.value)}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    >
                                        {statusOptions.map(option => (
                                            <option key={option.id} value={option.id}>
                                                {option.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Column 5: Actions */}
                                <div className="flex gap-2 items-end">
                                    <button
                                        onClick={applyFilters}
                                        className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-200 text-sm"
                                    >
                                        Apply
                                    </button>
                                    <button
                                        onClick={resetFilters}
                                        className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 text-sm"
                                    >
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Results Count */}
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mb-4">
                        <p className="text-sm text-gray-600">
                            Showing <span className="font-semibold text-gray-900">{filteredTickets.length}</span> tickets
                            {pagination.totalRecords > 0 && ` of ${pagination.totalRecords}`}
                        </p>
                        {loading && (
                            <div className="flex items-center text-sm text-gray-500">
                                <svg className="animate-spin h-4 w-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                                Loading...
                            </div>
                        )}
                    </div>

                    {/* Tickets Table */}
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Ticket ID
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Query Type
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Description
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            User
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Last Action
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            Date
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredTickets.length > 0 ? (
                                        filteredTickets.map((ticket) => (
                                            <tr key={ticket.ticket_id} className="hover:bg-gray-50 transition-colors duration-150">
                                                <td className="px-4 py-1 whitespace-nowrap text-sm font-medium text-blue-600 cursor-pointer" onClick={() => handleTicketClick(ticket)}>
                                                    #{ticket.ticket_id}
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getQueryColor(ticket.query)}`}>
                                                        {ticket.query}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                                                    {ticket.description}
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex flex-col text-gray-600">
                                                        <span>
                                                            {ticket.user_name || 'N/A'}
                                                        </span>
                                                        <span>
                                                            {ticket.user_mobile || 'N/A'}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getActionColor(ticket.last_action)}`}>
                                                        {ticket.last_action}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(ticket.status)}`}>
                                                        {ticket.status}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="flex flex-col">
                                                        <span className="text-sm text-gray-600">{ticket.date || 'N/A'}</span>
                                                        <span className="text-xs text-gray-400">{ticket.time || ''}</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                                                <div className="flex flex-col items-center">
                                                    <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                                                    </svg>
                                                    <p className="text-sm">No tickets found</p>
                                                    <p className="text-xs text-gray-400 mt-1">Try adjusting your filters</p>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Pagination */}
                    {pagination.totalPages > 1 && (
                        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6">
                            <div className="text-sm text-gray-600">
                                Page {pagination.currentPage} of {pagination.totalPages}
                            </div>
                            <div className="flex gap-2">
                                <button
                                    onClick={() => handlePageChange(pagination.currentPage - 1)}
                                    disabled={pagination.currentPage === 1}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === 1
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                >
                                    Previous
                                </button>

                                <div className="flex gap-1">
                                    {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                                        let pageNum;
                                        if (pagination.totalPages <= 5) {
                                            pageNum = i + 1;
                                        } else if (pagination.currentPage <= 3) {
                                            pageNum = i + 1;
                                        } else if (pagination.currentPage >= pagination.totalPages - 2) {
                                            pageNum = pagination.totalPages - 4 + i;
                                        } else {
                                            pageNum = pagination.currentPage - 2 + i;
                                        }

                                        return (
                                            <button
                                                key={pageNum}
                                                onClick={() => handlePageChange(pageNum)}
                                                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === pageNum
                                                    ? 'bg-blue-600 text-white'
                                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                    }`}
                                            >
                                                {pageNum}
                                            </button>
                                        );
                                    })}
                                </div>

                                <button
                                    onClick={() => handlePageChange(pagination.currentPage + 1)}
                                    disabled={pagination.currentPage === pagination.totalPages}
                                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${pagination.currentPage === pagination.totalPages
                                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        : 'bg-blue-600 text-white hover:bg-blue-700'
                                        }`}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Ticket Details Modal */}
            {ticketModalOpen && selectedTicket && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={() => setTicketModalOpen(false)}></div>
                    <div className="flex min-h-full items-center justify-center p-4">
                        <div className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                            {/* Header */}
                            <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center">
                                <div>
                                    <h3 className="text-xl font-semibold text-gray-900">Ticket Details</h3>
                                    <p className="text-sm text-gray-500">Ticket #{selectedTicket.ticket_id}</p>
                                </div>
                                <button
                                    onClick={() => setTicketModalOpen(false)}
                                    className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                                >
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            {/* Content */}
                            <div className="flex-1 overflow-y-auto p-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Ticket ID</label>
                                            <p className="mt-1 text-sm font-semibold text-blue-600">#{selectedTicket.ticket_id}</p>
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Query Type</label>
                                            <p className="mt-1">
                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getQueryColor(selectedTicket.query)}`}>
                                                    {selectedTicket.query}
                                                </span>
                                            </p>
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Description</label>
                                            <p className="mt-1 text-sm text-gray-900 bg-gray-50 p-3 rounded-lg break-words">{selectedTicket.description}</p>
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Status</label>
                                            <p className="mt-1">
                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(selectedTicket.status)}`}>
                                                    {selectedTicket.status}
                                                </span>
                                            </p>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">User</label>
                                            <div className="mt-1 space-y-1">
                                                <p className="text-sm text-gray-900 flex items-center gap-2">
                                                    <FaUser className="text-xs text-gray-400" />
                                                    {selectedTicket.user_name || 'N/A'}
                                                </p>
                                                <p className="text-sm text-gray-600 flex items-center gap-2">
                                                    <FaPhone className="text-xs text-gray-400" />
                                                    {selectedTicket.user_mobile || 'N/A'}
                                                </p>
                                                <p className="text-sm text-gray-600 flex items-center gap-2">
                                                    <FaEnvelope className="text-xs text-gray-400" />
                                                    {selectedTicket.user_email || 'N/A'}
                                                </p>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Last Action</label>
                                            <p className="mt-1">
                                                <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getActionColor(selectedTicket.last_action)}`}>
                                                    {selectedTicket.last_action || 'N/A'}
                                                </span>
                                            </p>
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Date & Time</label>
                                            <p className="mt-1 text-sm text-gray-900">{selectedTicket.date} {selectedTicket.time}</p>
                                        </div>
                                        <div>
                                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned To</label>
                                            <p className="mt-1 text-sm text-gray-900">{selectedTicket.assign_to || 'Not Assigned'}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="flex-shrink-0 bg-white border-t border-gray-200 px-6 py-3 flex justify-end">
                                <button
                                    onClick={() => setTicketModalOpen(false)}
                                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors duration-200 text-sm"
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Toaster position="top-right" />
        </div>
    );
}

export default Tickets;